import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useRecoilValue } from 'recoil';
import { endpointState } from '../../recoil/atoms';

interface Author {
	id?: number;
	name?: string;
	photo?: string;
}

interface PublicationData {
	id: number;
	title?: string;
	body?: string;
	image?: string | null;
	author?: Author;
	publication_date?: string;
	publication_hour?: string;
	app_ios?: string;
	app_android?: string;
}

const PublicFeedPublication: React.FC = () => {
	const { publicationId } = useParams<{ publicationId: string }>();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const developmentId = queryParams.get('d');

	const { urlEndpoint } = useRecoilValue(endpointState);
	const baseUrl = urlEndpoint || process.env.REACT_APP_API_URL || 'https://api.clubcombourbano.com/api';

	const [publication, setPublication] = useState<PublicationData | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let isMounted = true;
		setLoading(true);
		setError(null);

		const requestUrl = `${baseUrl}/feed/${publicationId}${developmentId ? `?d=${developmentId}` : ''}`;

		axios
			.get(requestUrl)
			.then((response) => {
				if (!isMounted) return;
				if (response.data && response.data.data) {
					setPublication(response.data.data);
				} else {
					setError('Publicação não encontrada.');
				}
				setLoading(false);
			})
			.catch((err) => {
				if (!isMounted) return;
				console.error('Erro ao carregar publicação:', err);
				setError('Não foi possível carregar a publicação.');
				setLoading(false);
			});

		return () => {
			isMounted = false;
		};
	}, [baseUrl, publicationId, developmentId]);

	useEffect(() => {
		if (!publication) return;

		const originalTitle = document.title;
		if (publication.title) {
			document.title = publication.title;
		}

		const setMetaTag = (property: string, content?: string) => {
			if (!content) return;
			let element = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
			if (!element) {
				element = document.createElement('meta');
				element.setAttribute('property', property);
				document.head.appendChild(element);
			}
			element.setAttribute('content', content);
		};

		const setNamedMeta = (name: string, content?: string) => {
			if (!content) return;
			let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
			if (!element) {
				element = document.createElement('meta');
				element.setAttribute('name', name);
				document.head.appendChild(element);
			}
			element.setAttribute('content', content);
		};

		const previewText = publication.body ? truncateBody(publication.body, 150) : publication.title || '';
		const previewImage = publication.image || publication.author?.photo || '';

		// Open Graph meta tags for WhatsApp & social link previews
		setMetaTag('og:title', publication.title || 'Publicação');
		setMetaTag('og:description', previewText);
		setMetaTag('og:image', previewImage);
		setMetaTag('og:url', window.location.href);
		setMetaTag('og:type', 'article');
		setMetaTag('og:site_name', 'Club Combo Urbano');

		// Twitter Card meta tags
		setNamedMeta('twitter:card', 'summary_large_image');
		setNamedMeta('twitter:title', publication.title || 'Publicação');
		setNamedMeta('twitter:description', previewText);
		setNamedMeta('twitter:image', previewImage);
		setNamedMeta('description', previewText);

		return () => {
			document.title = originalTitle;
		};
	}, [publication]);

	const formatDate = (dateStr?: string) => {
		if (!dateStr) return '';
		const parts = dateStr.split('-');
		if (parts.length === 3) {
			return `${parts[2]}/${parts[1]}/${parts[0]}`;
		}
		return dateStr;
	};

	const truncateBody = (text?: string, limit: number = 150) => {
		if (!text) return '';
		if (text.length <= limit) return text;
		return text.slice(0, limit);
	};

	return (
		<div className="min-h-screen bg-gray-100 flex flex-col items-center justify-between font-sans">
			{/* Main Content Area */}
			<div className="w-full max-w-md bg-white min-h-screen sm:min-h-0 sm:my-6 sm:rounded-xl sm:shadow-lg overflow-hidden flex flex-col justify-between">
				{loading ? (
					<div className="flex flex-col items-center justify-center p-12 text-gray-500 min-h-64">
						<i className="fas fa-spinner fa-spin text-3xl mb-3 text-indigo-600" />
						<p className="text-sm font-medium">Carregando publicação...</p>
					</div>
				) : error ? (
					<div className="flex flex-col items-center justify-center p-8 text-center min-h-64">
						<i className="fas fa-exclamation-circle text-4xl text-red-500 mb-3" />
						<p className="text-gray-800 font-semibold text-base mb-1">{error}</p>
						<p className="text-gray-500 text-sm">Verifique o link ou tente novamente mais tarde.</p>
					</div>
				) : publication ? (
					<div>
						{/* Author Header */}
						<div className="flex items-center justify-between p-4 border-b border-gray-100">
							<div className="flex items-center space-x-3">
								{publication.author?.photo ? (
									<img
										src={publication.author.photo}
										alt={publication.author.name || 'Autor'}
										className="w-10 h-10 rounded-full object-cover border border-gray-200"
									/>
								) : (
									<div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm">
										{publication.author?.name ? publication.author.name.charAt(0).toUpperCase() : 'U'}
									</div>
								)}
								<div>
									<h3 className="font-semibold text-gray-900 text-sm leading-tight">
										{publication.author?.name || 'Usuário'}
									</h3>
									{publication.publication_date && (
										<span className="text-xs text-gray-500">
											{formatDate(publication.publication_date)}
										</span>
									)}
								</div>
							</div>
							<button type="button" className="text-gray-400 hover:text-gray-600 focus:outline-none p-1">
								<i className="fas fa-ellipsis-v text-base" />
							</button>
						</div>

						{/* Publication Image */}
						{publication.image && (
							<div className="w-full bg-gray-50 overflow-hidden">
								<img
									src={publication.image}
									alt={publication.title || 'Imagem da publicação'}
									className="w-full h-auto max-h-[500px] object-cover"
								/>
							</div>
						)}

						{/* Content Section */}
						<div className="p-4 space-y-3">
							{publication.title && (
								<h1 className="text-base font-bold text-gray-900 leading-snug">
									{publication.title}
								</h1>
							)}

							{publication.body && (
								<div className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
									<p>
										{truncateBody(publication.body, 150)}
										{publication.body.length > 150 && '...'}
									</p>
									{publication.body.length > 150 && (
										<p className="mt-3 font-semibold text-indigo-600 italic">
											Baixe o App para continuar lendo.
										</p>
									)}
								</div>
							)}
						</div>
					</div>
				) : null}

				{/* Footer with App Download Buttons */}
				<div className="p-4 bg-gray-50 border-t border-gray-100 mt-auto">
					<p className="text-xs font-semibold text-gray-500 text-center uppercase tracking-wider mb-3">
						Baixe o aplicativo
					</p>
					<div className="flex flex-col sm:flex-row gap-2">
						{publication?.app_ios ? (
							<a
								href={publication.app_ios}
								target="_blank"
								rel="noopener noreferrer"
								style={{ backgroundColor: '#000000', color: '#ffffff' }}
								className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-xs shadow hover:opacity-90 transition-opacity cursor-pointer text-decoration-none"
							>
								<i className="fab fa-apple text-lg text-white" />
								<span className="text-white">App Store</span>
							</a>
						) : (
							<a
								href="#"
								style={{ backgroundColor: '#000000', color: '#ffffff' }}
								className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-xs shadow cursor-pointer text-decoration-none opacity-90"
							>
								<i className="fab fa-apple text-lg text-white" />
								<span className="text-white">App Store</span>
							</a>
						)}

						{publication?.app_android ? (
							<a
								href={publication.app_android}
								target="_blank"
								rel="noopener noreferrer"
								style={{ backgroundColor: '#01875f', color: '#ffffff' }}
								className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-xs shadow hover:opacity-90 transition-opacity cursor-pointer text-decoration-none"
							>
								<i className="fab fa-google-play text-base text-white" />
								<span className="text-white">Google Play</span>
							</a>
						) : (
							<a
								href="#"
								style={{ backgroundColor: '#01875f', color: '#ffffff' }}
								className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-xs shadow cursor-pointer text-decoration-none opacity-90"
							>
								<i className="fab fa-google-play text-base text-white" />
								<span className="text-white">Google Play</span>
							</a>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PublicFeedPublication;
